/**
 * Командная карточка заказа (team-portal): просмотр данных клиента (объём/состав — snapshotCommon
 * в Блоке 3 пуст), статус данных + этап «Ход работ», ответственный, документы. Командные действия:
 * возврат с комментарием, «Принять в работу», «Завершить», продвижение этапа (этап + ориент.дата +
 * файл через /team/files), правка объёма/ответственного, загрузка документов (тип + файл) и
 * «Выгрузить в Excel». Ввод структурированного «результата услуги» — отложено в Блоки 4/5/6.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { ArrowLeft, Download, Upload } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ApiError,
  teamApi,
  type StaffMember,
  type TeamOrderDetail,
  type TrainingSelectedEmployee,
} from '@/lib/api'
import { useAuth } from '@/app/auth-context'
import { getErrorMessage } from '@/lib/errors'
import { canAssign } from '@/team/roles'
import { staffOptions, useStaff } from '@/team/use-staff'
import {
  compositionSummary,
  hasRegistrationNumber,
  serviceLabel,
  teamStatusBadge,
  STAGES_BY_SERVICE,
  DOCUMENT_TYPES,
  type StageDef,
} from '@/lib/orders'
import { exportOrderToExcel } from '@/lib/excel'
import { CompanyProfileView } from '@/company/company-profile-view'
import { TeamSoutSection } from '@/sout/team-sout-section'
import { TeamProfriskSection } from '@/profrisk/team-profrisk-section'
import { TeamEcologySection } from '@/ecology/order/team-section'
import { TrainingOrderComposition } from '@/training/training-order-composition'
import { OrderInternalNotes } from '@/orders/order-internal-notes'
import { TrainingSubscriptionOrderBadge } from '@/training/training-subscription'
import {
  TrainingChangeEditor,
  TrainingChangeHistory,
  TrainingChangeNotice,
  type ChangeComposition,
} from '@/training/training-change'
import { compositionKey } from '@/training/training-request'
import { OrderStageStepper } from '@/orders/order-stage-stepper'
import { CancelOrderDialog, CancellationNotice, RestoreOrderDialog } from '@/orders/order-cancel'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { FileInput } from '@/design-system/components/primitives/file-input'
import { Alert } from '@/design-system/components/feedback/alert'
import { ListItem } from '@/design-system/components/data/list-item'
import { DocIcon } from '@/design-system/components/feedback/doc-icon'
import { Divider } from '@/design-system/components/layout/divider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const NONE = 'none'

/** Стили текстового поля (textarea) — те же токены, что у Input. */
const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Единый блок «Ход работ» (centrika-docs!72, перекомпонован в !82): сверху степпер этапов —
 * единственное представление их порядка/состояния (см. `OrderStageStepper`). Под ним, после
 * разделителя, — секция «Текущий этап» (форма смены текущего этапа `StageForm`); ниже, после
 * разделителя, — секция «Статус заказа» (принять/завершить, `StatusActions`). Разделены
 * управление ЭТАПОМ и статусом всего ЗАКАЗА. Логика продвижения этапа, принятия и блокировки
 * завершения не менялась — только состав и расположение UI.
 */
function WorkProgress({
  order,
  onChanged,
}: {
  order: TeamOrderDetail
  onChanged: (o: TeamOrderDetail) => void
}) {
  return (
    <Card header="Ход работ">
      <OrderStageStepper stages={order.stages} />

      <Divider className="my-4" />
      <h3 className="mb-3 font-semibold">Текущий этап</h3>
      {/* Форма смены текущего этапа. key сбрасывает её локальное состояние при смене текущего этапа.
          Отменённый заказ только просматривается (order-cancel-restore). */}
      {order.dataStatus === 'cancelled' ? (
        <p className="text-14 text-txt-secondary">
          Заказ отменён — этапы меняются после восстановления заказа.
        </p>
      ) : (
        <StageForm
          key={order.stages.find((s) => s.isCurrent)?.key ?? 'none'}
          order={order}
          onChanged={onChanged}
        />
      )}

      <StatusActions order={order} onChanged={onChanged} />
    </Card>
  )
}

/**
 * «Реквизиты на дату создания заказа» (F2-07, замечание менеджера): read-only снимок
 * реквизитов организации на момент создания заказа (не живой профиль клиента) — команда
 * видит ТО ЖЕ значение, что и клиент. Полный стабильный набор полей, пустые — «—».
 * Отсутствует у заказов клиента-физлица (реквизитов организации не бывает).
 */
function OrderRequisitesSnapshot({ order }: { order: TeamOrderDetail }) {
  if (!order.snapshotRequisites) return null
  return (
    <CompanyProfileView
      profile={order.snapshotRequisites}
      title="Реквизиты на дату создания заказа"
      hint="Зафиксированы при создании заказа и не меняются последующей правкой профиля компании."
      collapsible
      defaultCollapsed
    />
  )
}

/**
 * «Регистрационный номер» (centrika-docs!73) — карточка заказов СОУТ и профрисков
 * (profrisk-order-registration-number), перед «Составом заказа»; у обучения карточки нет
 * (`hasRegistrationNumber`). Номер присваивает лаборатория; необязательное текстовое поле,
 * значение = сохранённый рег.номер заказа либо пусто. Пустое значение сохраняется как отсутствие
 * рег.номера. Во время сохранения кнопка недоступна и показывает «Сохранение…»; при ошибке над
 * полем — сообщение.
 */
function RegistrationNumberCard({
  order,
  onChanged,
}: {
  order: TeamOrderDetail
  onChanged: (o: TeamOrderDetail) => void
}) {
  const [value, setValue] = useState(order.registrationNumber ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const saved = await teamApi.orders.saveRegistrationNumber(order.id, value.trim())
      // Синхронизируем поле с сохранённым (обрезанным) значением, чтобы не оставались случайные
      // пробелы: сервер вернул канон, локальный ввод приводим к нему.
      setValue(saved.registrationNumber ?? '')
      onChanged(saved)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить регистрационный номер'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card header="Регистрационный номер" className="mb-4">
      <form onSubmit={submit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}
        <Field label="Рег. номер" hint="Номер присваивает лаборатория.">
          {(props) => (
            <Input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
          )}
        </Field>
        <Button type="submit" size="sm" variant="secondary" disabled={busy}>
          {busy ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </form>
    </Card>
  )
}

function StatusActions({
  order,
  onChanged,
}: {
  order: TeamOrderDetail
  onChanged: (o: TeamOrderDetail) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function setStatus(status: 'in_progress' | 'done') {
    setError(null)
    setBusy(true)
    try {
      onChanged(await teamApi.orders.setStatus(order.id, status))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сменить статус'))
    } finally {
      setBusy(false)
    }
  }

  // Обучение (centrika-docs!41): отдельного поля даты здесь больше нет — дата окончания
  // обучения и итоговый документ заполняются у КАЖДОЙ программы в составе заказа ниже.
  // Пока они не заполнены, сервер отдаёт `resultsReady:false` и «Завершить заказ» недоступна.
  //
  // Секция «Статус заказа» (centrika-docs!82): управление статусом ВСЕГО заказа отделено от
  // управления этапом. Текст состояния и основная кнопка — друг под другом, по левому краю.
  // Показываем секцию только когда есть доступное действие (принять/завершить); у завершённого
  // или черновика — ни того ни другого, секция не рисуется (поведение как раньше).
  if (!order.canAccept && !order.canComplete) return null
  const waitsResults = order.canComplete && !order.resultsReady
  // Изменения состава на согласовании (training-change-approval): принять в работу и завершить —
  // после решения по ним (сервер проверяет то же).
  const changePending = Boolean(order.trainingChange)
  return (
    <>
      <Divider className="my-4" />
      <h3 className="mb-3 font-semibold">Статус заказа</h3>
      <div className="flex flex-col items-start gap-2">
        {error ? (
          <Alert variant="danger" className="w-full">
            {error}
          </Alert>
        ) : null}
        {order.canAccept ? (
          <>
            <p className="text-14 text-txt-secondary">Заказ ещё не принят в работу</p>
            <Button
              size="sm"
              disabled={busy || changePending}
              onClick={() => void setStatus('in_progress')}
            >
              Принять заказ в работу
            </Button>
          </>
        ) : null}
        {order.canComplete ? (
          <>
            <p className="text-14 text-txt-secondary">Заказ находится в работе</p>
            <Button
              size="sm"
              disabled={busy || waitsResults || changePending}
              onClick={() => void setStatus('done')}
            >
              Завершить заказ
            </Button>
            {waitsResults && order.service === 'training' ? (
              <p className="text-12 text-txt-tertiary">
                Заполните дату окончания обучения и загрузите итоговый документ по каждой программе
                в составе заказа ниже.
              </p>
            ) : null}
            {waitsResults && order.service === 'ecology' ? (
              <p className="text-12 text-txt-tertiary">
                Внесите дату результата по каждой площадке заказа в разделе ниже.
              </p>
            ) : null}
          </>
        ) : null}
        {changePending ? (
          <p className="text-12 text-txt-tertiary">
            Изменения состава на согласовании — согласуйте или отклоните их в составе заказа ниже.
          </p>
        ) : null}
      </div>
    </>
  )
}

function ReturnForm({
  order,
  onChanged,
}: {
  order: TeamOrderDetail
  onChanged: (o: TeamOrderDetail) => void
}) {
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!order.canReturn) return null

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!comment.trim()) return
    setError(null)
    setBusy(true)
    try {
      onChanged(await teamApi.orders.return(order.id, comment.trim()))
      setComment('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось вернуть заказ'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card header="Вернуть клиенту на доработку">
      <form onSubmit={submit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}
        <Field label="Комментарий — что исправить" required>
          {(props) => (
            <textarea
              {...props}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className={TEXTAREA_CLASS}
              placeholder="Опишите, что нужно дополнить или исправить"
            />
          )}
        </Field>
        <Button type="submit" size="sm" variant="secondary" disabled={busy || !comment.trim()}>
          {busy ? 'Возврат…' : 'Вернуть на доработку'}
        </Button>
      </form>
    </Card>
  )
}

function EditForm({
  order,
  accounts,
  onChanged,
}: {
  order: TeamOrderDetail
  accounts: StaffMember[]
  onChanged: (o: TeamOrderDetail) => void
}) {
  // Объём заказа СОУТ (centrika-docs!63) считается автоматически по числу рабочих мест и
  // недоступен для ручного ввода: поле только для просмотра, при сохранении не отправляется
  // (volumeSummary опущен → сервер оставляет значение как есть). У обучения/профрисков — прежнее
  // редактируемое текстовое поле.
  const isSout = order.service === 'sout'
  const [volume, setVolume] = useState(order.volumeSummary)
  const [responsible, setResponsible] = useState(order.responsibleAccountId ?? NONE)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      onChanged(
        await teamApi.orders.update(order.id, {
          volumeSummary: isSout ? undefined : volume.trim(),
          // Ответственного назначают руководитель и администратор (team-access): поле уходит на
          // сервер, только когда его изменили; пустая строка снимает ответственного.
          responsibleAccountId:
            responsible === (order.responsibleAccountId ?? NONE)
              ? undefined
              : responsible === NONE
                ? ''
                : responsible,
        }),
      )
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить изменения'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card header="Правка данных заказа">
      <form onSubmit={submit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}
        <Field label="Объём заказа">
          {(props) =>
            isSout ? (
              // Только для просмотра: значение = число рабочих мест из «Состава заказа».
              <Input
                {...props}
                value={compositionSummary('sout', order.itemCount)}
                readOnly
                disabled
              />
            ) : (
              <Input {...props} value={volume} onChange={(e) => setVolume(e.target.value)} />
            )
          }
        </Field>
        {accounts.length > 0 ? (
          <Field label="Ответственный от Центрики">
            {() => (
              <Select value={responsible} onValueChange={setResponsible}>
                <SelectTrigger aria-label="Ответственный от Центрики">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Не назначен</SelectItem>
                  {/* Назначенный ранее сотрудник с отключённой записью остаётся в списке. */}
                  {staffOptions(
                    accounts,
                    order.responsibleAccountId
                      ? { id: order.responsibleAccountId, fullName: order.responsibleName ?? '' }
                      : undefined,
                  ).map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        ) : null}
        <Button type="submit" size="sm" variant="secondary" disabled={busy}>
          {busy ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </form>
    </Card>
  )
}

/**
 * Тип документа по умолчанию для файла этапа: на этапе «Договор» — «Договоры» (о договоре и счёте
 * клиент получает уведомление, training-order-filling), иначе — первый тип справочника.
 */
function stageDocumentType(stageKey: string): string {
  return stageKey === 'contract' ? 'contracts' : DOCUMENT_TYPES[0].code
}

function StageForm({
  order,
  onChanged,
}: {
  order: TeamOrderDetail
  onChanged: (o: TeamOrderDetail) => void
}) {
  // Варианты — этапы самого заказа: у заказа абонентского обслуживания нет «Сбора данных»
  // (ecology-order-stages), канон услуги предложил бы этап, которого у заказа нет.
  const stages: StageDef[] =
    order.stages.length > 0
      ? order.stages.map((s) => ({ key: s.key, label: s.label }))
      : STAGES_BY_SERVICE[order.service]
  const currentKey = order.stages.find((s) => s.isCurrent)?.key ?? stages[0].key
  const [stageKey, setStageKey] = useState(currentKey)
  const [targetDate, setTargetDate] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [docType, setDocType] = useState(() => stageDocumentType(currentKey))
  // Тип выбран вручную — смена этапа его не трогает.
  const [docTypeChosen, setDocTypeChosen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function changeStage(key: string) {
    setStageKey(key)
    if (!docTypeChosen) setDocType(stageDocumentType(key))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      let fileId: string | undefined
      if (file) {
        const uploaded = await teamApi.uploadFile({
          file,
          orderId: order.id,
          documentTypeCode: docType,
        })
        fileId = uploaded.id
      }
      onChanged(
        await teamApi.orders.advanceStage(order.id, {
          stageKey,
          targetDate: targetDate || undefined,
          fileId,
        }),
      )
      setFile(null)
      setTargetDate('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сменить этап'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <Field label="Этап" required>
        {(props) => (
          <Select value={stageKey} onValueChange={changeStage}>
            <SelectTrigger
              id={props.id}
              aria-label="Этап хода работ"
              aria-required={props['aria-required']}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stages.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Ориентировочная дата завершения">
        {(props) => <DateInput {...props} value={targetDate} onChange={setTargetDate} />}
      </Field>
      <Field label="Файл этапа (необязательно)">
        {(props) => <FileInput {...props} value={file} onChange={setFile} disabled={busy} />}
      </Field>
      <Field label="Тип документа">
        {(props) => (
          <Select
            value={docType}
            onValueChange={(v) => {
              setDocType(v)
              setDocTypeChosen(true)
            }}
          >
            <SelectTrigger id={props.id} aria-label="Тип документа">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPES.map((t) => (
                <SelectItem key={t.code} value={t.code}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <div className="flex justify-start">
        <Button type="submit" size="sm" variant="secondary" disabled={busy}>
          {busy ? 'Сохранение…' : 'Сохранить изменения этапа'}
        </Button>
      </div>
    </form>
  )
}

function UploadDocForm({
  orderId,
  defaultType,
  onUploaded,
}: {
  orderId: string
  defaultType: string
  onUploaded: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [docType, setDocType] = useState(defaultType)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!file) return
    setError(null)
    setBusy(true)
    try {
      await teamApi.uploadFile({ file, orderId, documentTypeCode: docType })
      setFile(null)
      onUploaded()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить документ'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate className="mt-3 border-t border-line pt-3">
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <Field label="Тип документа" required className="my-0">
        {(props) => (
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger
              id={props.id}
              aria-label="Тип загружаемого документа"
              aria-required={props['aria-required']}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPES.map((t) => (
                <SelectItem key={t.code} value={t.code}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Файл" required>
        {(props) => <FileInput {...props} value={file} onChange={setFile} disabled={busy} />}
      </Field>
      <Button
        type="submit"
        size="sm"
        variant="secondary"
        disabled={busy || !file}
        leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
      >
        {busy ? 'Загрузка…' : 'Загрузить документ'}
      </Button>
    </form>
  )
}

function OrderDocuments({ order, onReload }: { order: TeamOrderDetail; onReload: () => void }) {
  return (
    <Card header="Документы по заказу">
      {order.documents.length === 0 ? (
        <p className="py-2 text-txt-secondary">Документов пока нет.</p>
      ) : (
        order.documents.map((d) => (
          <ListItem
            key={d.id}
            leading={<DocIcon />}
            name={d.title}
            meta={`${d.typeName} · ${formatDate(d.date)}`}
            action={
              <Button asChild size="sm" variant="secondary">
                <a href={d.downloadUrl} download>
                  Скачать
                </a>
              </Button>
            }
          />
        ))
      )}
      {/* В заказ обучения команда прикладывает договор и счёт (training-order-filling, В16) —
          по умолчанию «Договоры»: о договоре и счёте клиент получает уведомление. Итоговые
          документы обучения загружаются в группах программ состава. */}
      {/* Отменённый заказ только просматривается (order-cancel-restore): документы — после
          восстановления. */}
      {order.dataStatus === 'cancelled' ? null : (
        <UploadDocForm
          orderId={order.id}
          defaultType={order.service === 'training' ? 'contracts' : DOCUMENT_TYPES[0].code}
          onUploaded={onReload}
        />
      )}
    </Card>
  )
}

/**
 * Изменения состава заказа обучения (training-change-approval; ТЗ п.1.4, В20): блок «Изменения на
 * согласовании» с решением по изменениям клиента (или «Отозвать» свои) либо «Изменить состав» —
 * редактор с выбором сотрудников клиента. Согласует любой сотрудник, которому виден заказ.
 */
function TeamTrainingChange({
  order,
  onChanged,
}: {
  order: TeamOrderDetail
  onChanged: (next: TeamOrderDetail) => void
}) {
  const [editor, setEditor] = useState<{
    initial: ChangeComposition
    employees: TrainingSelectedEmployee[]
    locked: Set<string>
    lockedPrograms: Set<string>
  } | null>(null)
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)
  // Редактор открыт как «Предложить свой вариант» (ответ заказчицы 04.11 к п.1.4): отправка отклоняет
  // изменения клиента и вносит свои одной операцией.
  const [counter, setCounter] = useState(false)
  // Появились или решены изменения на согласовании (в том числе после перечитки карточки другим
  // действием) — редактор с прежним составом закрывается: предложение — желаемый состав целиком.
  const changeAt = order.trainingChange?.at
  useEffect(() => {
    setEditor(null)
  }, [changeAt])

  async function open() {
    setOpenError(null)
    setOpening(true)
    try {
      const [wizard, composition] = await Promise.all([
        teamApi.training.getWizard(order.id),
        teamApi.training.getComposition(order.id),
      ])
      setEditor({
        initial: { programs: wizard.programs, packages: wizard.packages },
        employees: wizard.selectedEmployees,
        locked: new Set(
          (composition.items ?? [])
            .filter((it) => it.locked)
            .map((it) =>
              it.packageId
                ? compositionKey('package', it.packageId)
                : compositionKey('program', it.programId ?? ''),
            ),
        ),
        lockedPrograms: new Set(
          composition.groups.filter((g) => g.documentName || g.documentUrl).map((g) => g.programId),
        ),
      })
    } catch (err) {
      setOpenError(getErrorMessage(err, 'Не удалось открыть изменение состава'))
    } finally {
      setOpening(false)
    }
  }

  async function decide(action: 'approve' | 'reject' | 'withdraw', reason = '') {
    try {
      setEditor(null)
      onChanged(
        await (action === 'approve'
          ? teamApi.training.approveChange(order.id)
          : action === 'reject'
            ? teamApi.training.rejectChange(order.id, reason)
            : teamApi.training.withdrawChange(order.id)),
      )
    } catch (err) {
      // Решение уже приняли с другой стороны — карточка приходит в соответствие с сервером.
      if (err instanceof ApiError && err.status === 409) {
        teamApi.orders
          .get(order.id)
          .then(onChanged)
          .catch(() => undefined)
      }
      throw err
    }
  }

  // Пока состав для своего варианта загружается, блок изменений клиента остаётся на месте.
  const counterTo = counter && editor && order.trainingChange?.byClient ? order.trainingChange : undefined
  if (order.trainingChange && !counterTo) {
    return (
      <div className="mt-4 flex flex-col gap-2">
        <TrainingChangeNotice
          change={order.trainingChange}
          side="team"
          onAction={decide}
          onCounter={
            opening
              ? undefined
              : () => {
                  setCounter(true)
                  void open()
                }
          }
        />
        {opening ? <p className="text-13 text-txt-secondary">Загрузка состава…</p> : null}
        {openError ? <Alert variant="danger">{openError}</Alert> : null}
      </div>
    )
  }
  if (editor) {
    return (
      <div className="mt-4">
        <TrainingChangeEditor
          counterTo={counterTo}
          orderId={order.id}
          side="team"
          submitted={order.dataStatus !== 'filling'}
          initial={editor.initial}
          employees={editor.employees}
          locked={editor.locked}
          lockedPrograms={editor.lockedPrograms}
          loadEmployees={(target, signal) =>
            target.kind === 'package'
              ? teamApi.training.listPackageEmployees(order.id, target.id, signal)
              : teamApi.training.listProgramEmployees(order.id, target.id, signal)
          }
          propose={async (items, reason) => {
            let next: TeamOrderDetail
            try {
              next = counterTo
                ? await teamApi.training.counterChange(order.id, items, reason ?? '')
                : await teamApi.training.proposeChange(order.id, items)
            } catch (err) {
              // 409 — заказ изменился с другой стороны (например, клиент уже предложил изменения):
              // карточка перечитывается и показывает его как есть, редактор закрывается.
              if (err instanceof ApiError && err.status === 409) {
                try {
                  const fresh = await teamApi.orders.get(order.id)
                  setEditor(null)
                  onChanged(fresh)
                  return
                } catch {
                  // Перечитать не удалось — ошибка остаётся в редакторе.
                }
              }
              throw err
            }
            setEditor(null)
            setCounter(false)
            onChanged(next)
          }}
          onCancel={() => {
            setEditor(null)
            setCounter(false)
          }}
        />
      </div>
    )
  }
  if (!order.canProposeChange) return null
  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" disabled={opening} onClick={() => void open()}>
          {opening ? 'Загрузка…' : 'Изменить состав'}
        </Button>
        <span className="text-13 text-txt-secondary">
          Добавить или убрать программу, изменить число мест, заменить слушателя — изменения
          согласует клиент.
        </span>
      </div>
      {openError ? <Alert variant="danger">{openError}</Alert> : null}
    </div>
  )
}

export function TeamOrderDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<TeamOrderDetail | null>(null)
  // Справочник сотрудников для выбора ответственного — руководителю и администратору
  // (team-access); у остальных список пуст и поле выбора не показывается.
  const { context } = useAuth()
  const accounts = useStaff(canAssign(context?.user.role))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Отмена и восстановление заказа (order-cancel-restore): окна подтверждения.
  const [cancelOpen, setCancelOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)
  // Изменения состава (training-change-approval): после решения по ним состав перечитывается.
  const [compositionVersion, setCompositionVersion] = useState(0)

  function load(signal?: AbortSignal) {
    if (!id) return
    setLoading(true)
    setError(null)
    teamApi.orders
      .get(id, signal)
      .then((res) => {
        if (!signal?.aborted) setOrder(res)
      })
      .catch((err) => {
        if (!signal?.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false)
      })
  }

  /**
   * Тихое обновление карточки заказа — без флага `loading` (страница не подменяется на
   * «Загрузка…»). Нужно после правки реквизитов программы обучения: меняется только
   * `resultsReady` (доступность «Завершить»), а перерисовка всей страницы сбросила бы
   * незасохранённый ввод соседних форм («Вернуть на доработку», «Продвинуть этап»).
   */
  function refresh() {
    if (!id) return
    teamApi.orders
      .get(id)
      .then(setOrder)
      .catch(() => undefined)
  }

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const badge = order ? teamStatusBadge(order.dataStatus) : null

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/team/orders')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К очереди заказов
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !order ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <PageHead
            title={
              <span className="flex items-center gap-2.5">
                {order.ownerName} — {serviceLabel(order.service)}
                {badge ? <Badge status={badge.role}>{badge.label}</Badge> : null}
              </span>
            }
            subtitle={
              // Номер и дата создания заказа (centrika-docs!47), под ними — ответственный.
              <>
                <span className="block">
                  Заказ № {order.number} от {formatDate(order.createdAt)}
                </span>
                {order.trainingSubscription ? (
                  <span className="my-1 block">
                    <TrainingSubscriptionOrderBadge />
                  </span>
                ) : null}
                <span className="block">
                  Ответственный: {order.responsibleName ?? 'не назначен'}
                  {/* Контакты из справочника сотрудников (team-access). */}
                  {[order.responsiblePhone, order.responsibleEmail].filter(Boolean).map((c) => (
                    <span key={c}> · {c}</span>
                  ))}
                </span>
              </>
            }
            actions={
              <div className="flex flex-wrap gap-2">
                {order.canCancel ? (
                  <Button variant="secondary" onClick={() => setCancelOpen(true)}>
                    Отменить заказ
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
                  onClick={() => exportOrderToExcel(order)}
                >
                  Выгрузить в Excel
                </Button>
              </div>
            }
          />

          {order.cancellation ? (
            <CancellationNotice
              cancellation={order.cancellation}
              side="team"
              canRestore={order.canRestore === true}
              onRestore={() => setRestoreOpen(true)}
              className="mb-4"
            />
          ) : null}
          <CancelOrderDialog
            open={cancelOpen}
            side="team"
            orderNumber={order.number}
            onClose={() => setCancelOpen(false)}
            onConfirm={async (reason) => {
              try {
                setOrder(await teamApi.orders.cancel(order.id, reason))
                setCancelOpen(false)
              } catch (err) {
                // Заказ могли принять, завершить или отменить с другой стороны — перечитываем.
                refresh()
                throw err
              }
            }}
          />
          <RestoreOrderDialog
            open={restoreOpen}
            fromStatus={order.cancellation?.fromStatus}
            onClose={() => setRestoreOpen(false)}
            onConfirm={async () => {
              try {
                setOrder(await teamApi.orders.restore(order.id))
                setRestoreOpen(false)
              } catch (err) {
                refresh()
                throw err
              }
            }}
          />

          {order.dataStatus === 'changes_requested' && order.returnComment ? (
            <Alert variant="info" title="Возвращён клиенту" className="mb-4">
              {order.returnComment}
            </Alert>
          ) : null}

          {hasRegistrationNumber(order.service) ? (
            <RegistrationNumberCard order={order} onChanged={setOrder} />
          ) : null}

          {/* У обучения состав со слушателями — ниже («Состав заказа обучения»); отдельная
              карточка с текстом объёма его дублировала (сверка ТЗ 26.10, п.6.2). */}
          {order.service === 'training' ? null : (
            <Card header="Состав заказа" className="mb-4">
              {/* СОУТ (centrika-docs!63): объём = число рабочих мест числительным (0 рабочих мест
                  у пустого состава), пересчитывается при загрузке заказа; экология (ecology-order) —
                  число площадок заказа; прочие услуги — текстовая сводка объёма («Объём не указан»,
                  если пусто). */}
              <p className="text-txt">
                {order.service === 'sout' || order.service === 'ecology'
                  ? compositionSummary(order.service, order.itemCount)
                  : order.volumeSummary || 'Объём не указан'}
              </p>
            </Card>
          )}

          <Grid cols={2}>
            <div className="flex flex-col gap-4">
              <WorkProgress order={order} onChanged={setOrder} />
              <OrderRequisitesSnapshot order={order} />
            </div>
            <OrderDocuments order={order} onReload={() => load()} />
          </Grid>

          {/* Обучение (ТЗ п.6.3, ответ В24 — «у обоих»): состав со слушателями и изменения состава —
              сразу под «Ходом работ» и документами, как на странице клиента; служебные формы — ниже. */}
          {order.service === 'training' ? (
            <TeamTrainingChange
              order={order}
              onChanged={(next) => {
                setOrder(next)
                setCompositionVersion((v) => v + 1)
              }}
            />
          ) : null}
          {order.service === 'training' ? (
            <TrainingOrderComposition
              // Право править результаты зависит от статуса заказа и приходит вместе с составом:
              // после «Принять заказ в работу» состав перечитывается, иначе поля даты и итогового
              // документа появлялись только после перезагрузки страницы. После решения по
              // изменениям состава (training-change-approval) — тоже.
              key={`${order.dataStatus}-${compositionVersion}`}
              orderId={order.id}
              mode="team"
              className="mt-4"
              onResultsChanged={refresh}
            />
          ) : null}
          {/* История изменений состава (ответ заказчицы 04.11 к п.1.4). */}
          <TrainingChangeHistory history={order.trainingChangeHistory} side="team" className="mt-4" />

          {/* Внутренние комментарии команды (order-internal-notes; ТЗ п.9.5) — клиенту не видны;
              и у отменённого заказа: это заметки команды, не правка заказа. */}
          <OrderInternalNotes key={order.id} orderId={order.id} className="mt-4" />

          {/* Отменённый заказ не правится (order-cancel-restore) — до восстановления формы нет. */}
          {order.dataStatus === 'cancelled' ? null : (
            <Grid cols={2} className="mt-4">
              <EditForm order={order} accounts={accounts} onChanged={setOrder} />
              <ReturnForm order={order} onChanged={setOrder} />
            </Grid>
          )}

          {order.service === 'sout' ? (
            // Раздел сам читает статус заказа и по нему решает, доступна ли форма результатов:
            // при смене статуса перечитывается, иначе после «Принять заказ в работу» форма
            // оставалась «недоступна» до перезагрузки страницы.
            <TeamSoutSection key={order.dataStatus} orderId={order.id} />
          ) : null}
          {order.service === 'profrisk' ? (
            // Перечитывается при смене статуса: отменённый заказ только просматривается
            // (order-cancel-restore).
            <TeamProfriskSection key={order.dataStatus} orderId={order.id} />
          ) : null}
          {order.service === 'ecology' ? (
            <TeamEcologySection
              orderId={order.id}
              status={order.dataStatus}
              onResultsSaved={() => load()}
            />
          ) : null}
        </>
      )}
    </div>
  )
}
